// @ts-check
import { useMemo } from 'react';

import { useVideoPlayer } from 'Context/VideoPlayer';
import { useMindstampVideoPlayer, PLAYER_STATE } from 'Context/MindstampVideoPlayer';
import { useLearnCraftVideoPlayer } from 'Context/LearnCraftVideoPlayer';

/** @returns {ClassroomVideoPlayer} */
function useClassroomVideoPlayer() {
  const { player, playerRef, videoState, play, pause } = useVideoPlayer();
  const {
    playerState: mindstampPlayerState,
    videoState: mindstampVideoState,
    currentTimeRef,
    play: mindstampPlay,
    pause: mindstampPause,
  } = useMindstampVideoPlayer();

  const {
    playerState: learnCraftPlayerState,
    videoState: learnCraftVideoState,
    getCurrentTime,
    play: learnCraftPlay,
    pause: learnCraftPause,
  } = useLearnCraftVideoPlayer();

  const videoPlayer = useMemo(
    /** @return {ClassroomVideoPlayer} */
    () => {
      if (player) {
        return {
          hasPlayer: true,
          videoState,
          getCurrentTime: playerRef.getCurrentTime,
          play,
          pause,
        };
      } else if (mindstampPlayerState === PLAYER_STATE.ACTIVE) {
        return {
          hasPlayer: true,
          videoState: mindstampVideoState,
          getCurrentTime: () => currentTimeRef.current,
          play: mindstampPlay,
          pause: mindstampPause,
        };
      } else if (learnCraftPlayerState === 'ACTIVE') {
        return {
          hasPlayer: true,
          videoState: learnCraftVideoState,
          getCurrentTime,
          play: learnCraftPlay,
          pause: learnCraftPause,
        };
      }

      // default value
      return {
        hasPlayer: false,
        videoState: null,
        getCurrentTime: () => {},
        play: () => {},
        pause: () => {},
      };
    },
    [
      playerRef,
      videoState,
      mindstampPlayerState,
      mindstampVideoState,
      learnCraftPlayerState,
      learnCraftVideoState,
    ]
  );

  return videoPlayer;
}

export default useClassroomVideoPlayer;

/**
 * @typedef {typeof import('Context/constant').VIDEO_STATE} videoStateType
 * @typedef ClassroomVideoPlayer
 * @property {boolean} hasPlayer
 * @property {?videoStateType[keyof videoStateType]} videoState
 * @property {function(): number | void} getCurrentTime
 * @property {function(): void} play
 * @property {function(): void} pause
 */
